import { ROUTES } from '@/lib/constants/routes';
import type { SavedAudience } from './audience-definition';
import { withClient } from './client-param';

/** What the Intelligence page last had open, and for whom. */
export interface IntelligenceView {
  /** The signed-in user it belongs to: another login never inherits it. */
  userId: string;
  /** The open audience; `undefined` on a screen with none (setup, no audience yet). */
  audienceId: string | undefined;
  /** The client an internal user was acting for; `undefined` for a client user. */
  tenantId: string | undefined;
}

/** The remembered view, if it is this user's. */
export const viewFor = (
  view: IntelligenceView | null,
  userId: string | undefined,
): IntelligenceView | undefined => {
  if (!userId || view?.userId !== userId) return undefined;
  return view;
};

/**
 * The way back into Intelligence — the navbar, and onboarding's Skip and exit: the last open
 * audience with its client; else that client's landing; else the default landing.
 */
export const intelligenceHomePath = (view: IntelligenceView | undefined): string =>
  withClient(
    view?.audienceId ? ROUTES.intelligenceAudience(view.audienceId) : ROUTES.intelligenceAudiences,
    view?.tenantId,
  );

/**
 * A profile's way back to its audience. The remembered client rides along only when it was
 * remembered for this same audience — any other client's list would not hold it.
 */
export const audienceBackPath = (audienceId: string, view: IntelligenceView | undefined): string =>
  withClient(
    ROUTES.intelligenceAudience(audienceId),
    view?.audienceId === audienceId ? view.tenantId : undefined,
  );

/** The crumb's label until the audience's own name is known. */
export const AUDIENCE_CRUMB_FALLBACK = 'Audience';

/**
 * A profile's first crumb names the audience it belongs to (NPJB-828), not the product: the
 * crumb leads back to that audience. Before the list has loaded, or for an audience it does
 * not hold, the crumb still reads as the way back.
 */
export const audienceCrumbLabel = (
  audiences: readonly Pick<SavedAudience, 'id' | 'title'>[],
  audienceId: string,
): string => {
  const title = audiences.find((audience) => audience.id === audienceId)?.title.trim() ?? '';
  return title === '' ? AUDIENCE_CRUMB_FALLBACK : title;
};
